import { FormInputField, FormInputFieldStatus } from '../../../contracts/form-input-field.js';
import { MapUtils } from '../../../utility/map-utils.js';
import { SerializationContext, SerializedData, DataModelBuilder } from '../../dto/data-model.js';
import { isSameData } from '../../dto/data-model-utils.js';
import { LocalizedString } from '../localized-string.js';
import { VersionRange } from '../../core/version-range.js';
import { toSafeFilename } from '../../../utility/string.js';
import { fromEnumAsString, toEnumFromString } from '../../../serialization/enum-utils.js';

export class DateTimeFormInputField extends FormInputField {
  static readonly TYPE_ID: string = '4f18387516fa46d192cebb83c8970739';
  static readonly SCHEMA_VERSION: number = 3;

  constructor(
    public readonly uniqueId: string,
    public readonly titles: LocalizedString,
    public readonly descriptions: LocalizedString,
    public readonly isRequired: boolean,
    public readonly status: FormInputFieldStatus,
  ) {
    super();
  }

  get $typeId(): string {
    return DateTimeFormInputField.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.between(1, DateTimeFormInputField.SCHEMA_VERSION);
  }

  static fromMap(context: SerializationContext, map: SerializedData): DateTimeFormInputField {
    return new DateTimeFormInputField(
        MapUtils.getString(map, 'uniqueId'),
        context.fromData<LocalizedString>(MapUtils.get<SerializedData>(map, 'titles')),
        context.fromData<LocalizedString>(MapUtils.get<SerializedData>(map, 'descriptions')),
        MapUtils.getBoolean(map, 'isRequired'),
        toEnumFromString(MapUtils.getString(map, '$status'), FormInputFieldStatus),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'uniqueId': this.uniqueId,
      'titles': context.toData(this.titles),
      'descriptions': context.toData(this.descriptions),
      'isRequired': this.isRequired,
      '$status': fromEnumAsString(this.status),
    };
  }

  clone(): this {
    return new DateTimeFormInputField(
        this.uniqueId,
        this.titles.clone(),
        this.descriptions.clone(),
        this.isRequired,
        this.status,
    ) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return other.uniqueId === this.uniqueId &&
       isSameData(other.titles, this.titles) &&
       isSameData(other.descriptions, this.descriptions) &&
       other.isRequired === this.isRequired &&
       other.status === this.status;
  }
}

export class DateTimeFormInputFieldBuilder extends DataModelBuilder<DateTimeFormInputField> {
  build(context: SerializationContext, map: SerializedData): DateTimeFormInputField {
    return DateTimeFormInputField.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);

    if(versionRange.contains(DateTimeFormInputField.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(DateTimeFormInputField.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    // In this version `isRequired` of type `boolean` and `uniqueId` of type `string` was introduced.
    if(version < 2) {
      MapUtils.setIfNullOrUndefined(map, 'isRequired', true);
      MapUtils.setIfNullOrUndefined(map, 'uniqueId', toSafeFilename(
          context.fromData<LocalizedString>(MapUtils.getRecord(map, 'titles')).getAnyOrDefault({ defValue: 'default' }))
      );
      version = 2;
    }

    // In version 3 we added `$status` of type `FormInputFieldStatus`.
    if(version < 3) {
      MapUtils.setIfNullOrUndefined(map, '$status', FormInputFieldStatus.Live);
      version = 3;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
